import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import * as d3 from 'd3';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { CaseActions, CaseSelectors, GlobalSelectors } from '@app/store';
import { Router } from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
  selector: 'app-support-case-widget',
  templateUrl: './support-case-widget.component.html',
  styleUrls: ['./support-case-widget.component.scss'],
  encapsulation: ViewEncapsulation.None, // needed for the scss to apply to the classes set on d3 like the text
})
export class SupportCaseWidgetComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  isCaseStatusLoading$: Observable<boolean> = this.store.select(
    CaseSelectors.isCaseStatusLoading
  );
  height: number = 200;
  width: number = 200;
  totalCases: number = 0;
  data = {};
  caseTypeCodeToColorMap = {};
  svg;

  constructor(
    private store: Store,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store
        .select(GlobalSelectors.getSelectedGlobalFilters)
        .pipe(
          filter(
            selectedBillingAcc => selectedBillingAcc.billingAccounts.length > 0
          ),
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          })
        )
        .subscribe(selectedBillingAcc => {
          this.store.dispatch(CaseActions.caseStatusStartLoading());

          this.store.dispatch(
            CaseActions.getCaseTypeSummary({ requestBody: selectedBillingAcc })
          );

          this.store.dispatch(CaseActions.caseStatusStopLoading());
        })
    );

    this.subscriptions.add(
      combineLatest([
        this.store.select(CaseSelectors.getSupportCaseTypes),
        this.store.select(CaseSelectors.getCasesTypeSummary),
      ])
        .pipe(
          filter(
            ([supportCaseTypes, casesTypeSummary]) =>
              casesTypeSummary && supportCaseTypes.length > 0
          )
        )
        .subscribe(([supportCaseTypes, casesTypeSummary]) => {
          // reset fields
          this.svg = undefined;
          this.totalCases = 0;
          this.data = {};
          this.caseTypeCodeToColorMap = {};

          supportCaseTypes.forEach(caseType => {
            const foundCaseTypeSummaryItem = casesTypeSummary.find(
              caseTypeSummaryItem =>
                String(caseTypeSummaryItem.caseTypeCode) === caseType.id
            );

            if (foundCaseTypeSummaryItem) {
              if (foundCaseTypeSummaryItem.openCasesCount) {
                this.totalCases += foundCaseTypeSummaryItem.openCasesCount;
                this.data[caseType.name] =
                  foundCaseTypeSummaryItem.openCasesCount;
              }
            }
          });

          // draw the donut chart
          this.createSVG();
        })
    );
  }

  createSVG(): void {
    // remove any preexisting charts
    this.svg = undefined;
    d3.select('.chart-container svg').remove();

    // create dimensions and center chart
    this.svg = d3
      .select('figure#donut-chart')
      .append('svg')
      .attr('class', 'pie')
      .attr('height', this.height)
      .attr('width', this.width)
      .append('g')
      .attr('transform', `translate(${this.height / 2},${this.width / 2})`);

    // create center title text
    this.svg
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-20')
      .text(
        this.totalCases > 0
          ? 'Total Open Cases'
          : 'There Are No Open Cases To Display'
      )
      .attr('class', 'chart-title-text');

    // add total case count value to center
    if (this.totalCases > 0) {
      this.svg
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '20')
        .text(this.totalCases)
        .attr('class', 'chart-value-text');
    }

    const pie = d3.pie().value(d => d[1]);
    const arc = d3.arc().innerRadius(75).outerRadius(100);

    // Tooltip
    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', '#fff')
      .style('border', '1px solid #ccc')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('box-shadow', '0px 4px 6px rgba(0, 0, 0, 0.1)')
      .style('pointer-events', 'none'); // Prevent tooltip from blocking mouse events

    const combinedColorScheme = [
      '#98E1B8',
      '#00A476',
      '#8DDBE8',
      '#798996',
      '#FBA300',
      '#21409A',
      '#008F6B',
      '#6484e1',
      '#EF314E',
    ]
      .concat(d3.schemeSet2)
      .concat(d3.schemeSet3);

    const colorScale = d3.scaleOrdinal(combinedColorScheme);

    this.svg
      .selectAll('path')
      .data(pie(<any>Object.entries(this.data)))
      .join('path')
      .attr('d', arc)
      .attr('fill', (d: any): any => {
        const color = colorScale(d.data[0]);
        this.caseTypeCodeToColorMap[d.data[0]] = {
          color: color,
          count: d.data[1],
        };
        return color;
      })
      .on('mouseover', (_event, d: any) => {
        tooltip
          .html(
            `<strong style="color:${
              this.caseTypeCodeToColorMap[d.data[0]].color
            }">${d.data[0]}</strong><br>Count: <strong>${
              d.data[1]
            }</strong><br>Percentage: <strong>${(
              (d.data[1] / this.totalCases) *
              100
            ).toFixed(2)}%</strong>`
          )
          .style('visibility', 'visible');
      })
      .on('mousemove', event => {
        tooltip
          .style('top', `${event.pageY + 10}px`)
          .style('left', `${event.pageX + 10}px`);
      })
      .on('mouseout', () => {
        tooltip.style('visibility', 'hidden');
      });
  }

  rerouteOnClick(): void {
    this.router.navigate(['support-signed-in/support-ticket-list']);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
